import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ListRange } from '@angular/cdk/collections';
import { hasModifierKey } from '@angular/cdk/keycodes';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CampaignFormStore } from '@campaign-form/campaign-form.store';
import { EPlatform } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddPlatformDialogComponent } from '@pages/creators/creator-details/add-platform-dialog/add-platform-dialog.component';
import { ICreator } from '@pages/creators/creators.interface';
import { BudgetComponent } from '@shared/components/budget/budget.component';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { ALL_CREATORS_ID } from '@shared/constants/all-creators.constants';
import { uniqBy } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { fromEvent, merge } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { CreatorsAndCtasService } from '../creators-and-ctas.service';
import { CtaSelectService } from '../cta-select.service';
import {
  ICreatorAndBudgetDialogData,
  ICreatorAndBudgetDialogResult,
  ICreatorPickerRow,
  IScrollFades,
  ISelectedCreator,
} from './creator-and-budget-dialog.interface';
import { CreatorAndBudgetDialogStore } from './creator-and-budget-dialog.store';
import {
  countPayoutsToOverwrite,
  CREATOR_PICKER_LIST_ROW_HEIGHT,
  CREATOR_PICKER_TRAY_ROW_HEIGHT,
  filterCreatorRows,
  getCreatorPickerLayout,
  getRangeRows,
  getSelectableRows,
  getSelectAllState,
  toCreatorPickerRow,
} from './creator-and-budget-dialog.util';

@Component({
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatButtonModule,
    MatTooltipModule,
    ScrollingModule,
    NgxMatSelectSearchModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
    IconComponent,
    IconBadgeComponent,
    SideDialogComponent,
    BudgetComponent,
  ],
  providers: [CampaignFormStore, CreatorAndBudgetDialogStore],
  selector: 'app-creator-and-budget-dialog',
  styleUrl: './creator-and-budget-dialog.component.scss',
  templateUrl: './creator-and-budget-dialog.component.html',
})
export class CreatorAndBudgetDialogComponent {
  public readonly listRowHeight = CREATOR_PICKER_LIST_ROW_HEIGHT;
  public readonly trayRowHeight = CREATOR_PICKER_TRAY_ROW_HEIGHT;
  public readonly skeletonRows = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  public readonly skeletonOverlayTheme = { 'border-radius': '100vw', height: '100%', margin: '0', width: '100%' };

  public readonly searchControl = new FormControl<string>('', { nonNullable: true });
  public readonly platformFilterControl = new FormControl<EPlatform[]>([], { nonNullable: true });
  public readonly campaignFilterControl = new FormControl<string[]>([], { nonNullable: true });
  public readonly campaignSearchControl = new FormControl<string>('', { nonNullable: true });
  public readonly bulkCostControl = new FormControl<string | null>(null);

  public readonly hideAdded = signal<boolean>(true);
  public readonly selected = signal<ISelectedCreator[]>([]);
  public readonly trayCollapsed = signal<boolean>(false);
  public readonly listFades = signal<IScrollFades>({ bottom: false, top: false });
  public readonly trayFades = signal<IScrollFades>({ bottom: false, top: false });

  public readonly campaignFormStore = inject(CampaignFormStore);
  public readonly dialogStore = inject(CreatorAndBudgetDialogStore);
  private readonly data = inject<ICreatorAndBudgetDialogData>(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<CreatorAndBudgetDialogComponent>);
  private readonly creatorsAndCtasService = inject(CreatorsAndCtasService);
  private readonly ctaSelectService = inject(CtaSelectService);
  private readonly translate = inject(TranslateService);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly viewport = viewChild(CdkVirtualScrollViewport);
  private readonly pickerRoot = viewChild<ElementRef<HTMLElement>>('pickerRoot');
  private readonly listBody = viewChild<ElementRef<HTMLElement>>('listBody');
  private readonly trayRows = viewChild<ElementRef<HTMLElement>>('trayRows');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private readonly searchTerm = signal<string>('');
  public readonly platformFilter = toSignal(this.platformFilterControl.valueChanges, { initialValue: [] });
  public readonly campaignFilter = toSignal(this.campaignFilterControl.valueChanges, { initialValue: [] });
  private readonly campaignSearchTerm = toSignal(this.campaignSearchControl.valueChanges.pipe(debounceTime(200)), {
    initialValue: '',
  });
  private readonly availableHeight = signal<number | null>(null);
  private readonly newCreators = signal<ICreator[]>([]);
  private readonly activeRowIndex = signal<number>(0);
  private readonly renderedRange = signal<ListRange>({ end: 0, start: 0 });

  private lastClickedCreatorId: string | null = null;
  private dialogClosing = false;
  private confirmDialogOpen = false;

  public readonly campaign = computed(() => this.data.campaign);
  public readonly isEditMode = computed(() => !!this.data.creatorToEditId);
  public readonly hasPayout = computed(() => !!this.campaign()?.budget && !!this.campaign()?.currency);
  public readonly creatorsLoaded = computed(() => this.campaignFormStore.creators$() !== null);
  public readonly creatorsFailed = computed(
    () => this.campaignFormStore.creators$() === null && !this.campaignFormStore.creatorsLoading$(),
  );

  public readonly rows = computed(() => {
    const existingCreatorIds = this.existingCreatorIds();
    const creators = uniqBy([...this.newCreators(), ...(this.campaignFormStore.creators$() ?? [])], 'id').filter(
      (creator) => creator.id !== ALL_CREATORS_ID,
    );

    return creators
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((creator) => toCreatorPickerRow(creator, existingCreatorIds));
  });

  public readonly filteredRows = computed(() =>
    filterCreatorRows(this.rows(), {
      campaignCreatorIds: this.campaignCreatorIds(),
      hideAdded: this.hideAdded(),
      platforms: this.platformFilter(),
      term: this.searchTerm(),
    }),
  );

  public readonly selectedIds = computed(() => new Set(this.selected().map((item) => item.creator.id)));
  public readonly selectableCount = computed(() => getSelectableRows(this.filteredRows()).length);
  public readonly selectAllState = computed(() => getSelectAllState(this.filteredRows(), this.selectedIds()));
  // Roving tabindex: the active row if the virtual list has it rendered, otherwise the first rendered
  // row - so Tab never skips the list after scrolling away or filtering.
  public readonly focusableRowIndex = computed(() => {
    const lastIndex = Math.max(0, this.filteredRows().length - 1);
    const activeIndex = Math.min(this.activeRowIndex(), lastIndex);
    const { end, start } = this.renderedRange();

    return activeIndex >= start && activeIndex < end ? activeIndex : Math.min(start, lastIndex);
  });

  public readonly platformOptions = computed(() =>
    Object.values(EPlatform).filter((platform) => this.rows().some((row) => row.platforms.includes(platform))),
  );

  public readonly campaignOptions = computed(() => {
    const term = this.campaignSearchTerm().trim().toLowerCase();
    const selectedCampaignIds = this.campaignFilter();

    return (this.dialogStore.campaignOptions$() ?? []).filter(
      (option) => !term || option.name.toLowerCase().includes(term) || selectedCampaignIds.includes(option.id),
    );
  });

  public readonly hasCommonPlatforms = computed(
    () => this.ctaSelectService.findCommonPlatforms(this.selected().map((item) => item.creator)).length > 0,
  );

  public readonly layout = computed(() =>
    getCreatorPickerLayout({
      availableHeight: this.availableHeight() ?? 600,
      listRowCount: this.creatorsLoaded() ? this.rows().length : null,
      trayCollapsed: this.trayCollapsed(),
      trayRowCount: this.selected().length,
    }),
  );

  private readonly existingCreatorIds = computed(
    () =>
      new Set(
        this.data.campaignCreatorsValue.reduce((ids, campaignCreator) => {
          if (campaignCreator.creatorId && campaignCreator.creatorId !== this.data.creatorToEditId) {
            ids.push(campaignCreator.creatorId);
          }

          return ids;
        }, [] as string[]),
      ),
  );

  private readonly campaignCreatorIds = computed(() => {
    const selectedCampaignIds = this.campaignFilter();

    if (!selectedCampaignIds.length) {
      return null;
    }

    return new Set(
      (this.dialogStore.campaignOptions$() ?? [])
        .filter((option) => selectedCampaignIds.includes(option.id))
        .flatMap((option) => option.creatorIds),
    );
  });

  constructor() {
    if (this.isEditMode()) {
      this.initEditMode();
    } else {
      this.campaignFormStore.fetchCreators();
      this.subscribeToSearchChanges();
      this.dialogStore.fetchCampaignOptions(this.campaign()?.id ?? null);
      this.createSelectionAnnouncementEffect();
      this.createLayoutSync();
    }

    this.subscribeToEscape();
  }

  public onRetryCreatorsClick(): void {
    this.campaignFormStore.fetchCreators();
  }

  public onSelectAllCampaignsClick(): void {
    this.campaignFilterControl.setValue((this.dialogStore.campaignOptions$() ?? []).map((option) => option.id));
  }

  public onUnselectAllCampaignsClick(): void {
    this.campaignFilterControl.setValue([]);
  }

  public onHideAddedClick(): void {
    this.hideAdded.update((hideAdded) => !hideAdded);
  }

  public onSearchKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown') {
      return;
    }

    // Skip the debounce so the list being entered matches what was just typed.
    this.searchTerm.set(this.searchControl.value);

    if (!this.filteredRows().length) {
      return;
    }

    event.preventDefault();
    this.focusRow(this.focusableRowIndex());
  }

  public onRowClick(row: ICreatorPickerRow, index: number, event: MouseEvent): void {
    this.activeRowIndex.set(index);
    (event.currentTarget as HTMLElement | null)?.querySelector('input')?.focus({ preventScroll: true });

    if (row.alreadyAdded) {
      return;
    }

    const shouldSelect = !this.selectedIds().has(row.creator.id);
    const rangeRows = event.shiftKey
      ? getRangeRows(this.filteredRows(), this.lastClickedCreatorId, row.creator.id)
      : null;

    this.setRowsSelected(rangeRows ?? [row], shouldSelect);
    this.lastClickedCreatorId = row.creator.id;
  }

  public onListKeydown(event: KeyboardEvent): void {
    const rows = this.filteredRows();
    const currentIndex = Math.min(this.activeRowIndex(), rows.length - 1);
    const currentRow = rows[currentIndex];

    if (!currentRow) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.toggleRow(currentRow);

      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    event.preventDefault();

    if (event.key === 'ArrowUp' && currentIndex === 0 && !event.shiftKey) {
      this.searchInput()?.nativeElement.focus();

      return;
    }

    const nextIndex = Math.max(0, Math.min(currentIndex + (event.key === 'ArrowDown' ? 1 : -1), rows.length - 1));

    if (event.shiftKey) {
      this.setRowsSelected(getSelectableRows([currentRow, rows[nextIndex]]), true);
      this.lastClickedCreatorId = rows[nextIndex].creator.id;
    }

    this.focusRow(nextIndex);
  }

  // Keeps the keyboard anchor on whichever row actually received focus (e.g. a fallback row reached via Tab).
  public onListFocusIn(event: FocusEvent): void {
    const rowIndex = (event.target as HTMLElement).closest('[data-row-index]')?.getAttribute('data-row-index');

    if (rowIndex) {
      this.activeRowIndex.set(Number(rowIndex));
    }
  }

  public onSelectAllChange(): void {
    const selectableRows = getSelectableRows(this.filteredRows());

    this.setRowsSelected(selectableRows, this.selectAllState() !== 'all');
  }

  public onRemoveSelectedClick(creatorId: string): void {
    this.selected.update((selected) => selected.filter((item) => item.creator.id !== creatorId));
  }

  public onTrayToggleClick(): void {
    this.trayCollapsed.update((trayCollapsed) => !trayCollapsed);
  }

  public onTrayScroll(): void {
    this.updateTrayFades();
  }

  public onApplyToAllClick(): void {
    const cost = this.bulkCostControl.value;

    if (!cost) {
      return;
    }

    const overwriteCount = countPayoutsToOverwrite(
      this.selected().map((item) => item.cost.value),
      cost,
    );

    if (!overwriteCount) {
      this.applyCostToAll(cost);

      return;
    }

    this.openConfirmDialog(
      {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant(
          'pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.overwriteConfirm',
        ),
        message: this.translate.instant(
          this.countKey(
            'pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.overwriteMessage',
            overwriteCount,
          ),
          { count: overwriteCount },
        ),
        swapButtons: true,
        title: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.overwriteTitle'),
      },
      () => this.applyCostToAll(cost),
    );
  }

  public openCreatorCreateDialog(): void {
    const dialogRef = this.dialog.open(AddPlatformDialogComponent, {
      data: { creator: null, redirectToCreatorDetails: false },
    });

    dialogRef.afterClosed().subscribe((creator: ICreator | undefined) => {
      if (!creator?.id) {
        return;
      }

      this.newCreators.update((newCreators) => [creator, ...newCreators]);

      if (!this.existingCreatorIds().has(creator.id)) {
        this.selectCreators([creator]);
      }
    });
  }

  public onCancelClick(): void {
    if (this.dialogClosing || this.confirmDialogOpen) {
      return;
    }

    const selectedCount = this.selected().length;

    if (this.isEditMode() || !selectedCount) {
      this.closeDialog();

      return;
    }

    this.openConfirmDialog(
      {
        cancelText: this.translate.instant(
          'pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.discardCancel',
        ),
        confirmText: this.translate.instant(
          'pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.discardConfirm',
        ),
        message: this.translate.instant(
          this.countKey('pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.discardMessage', selectedCount),
          {
            count: selectedCount,
          },
        ),
        swapButtons: true,
        title: this.translate.instant('pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.discardTitle'),
      },
      () => this.closeDialog(),
    );
  }

  public onSubmitClick(continueWithDeliverables = false): void {
    if (this.dialogClosing || !this.selected().length) {
      return;
    }

    if (this.isEditMode()) {
      this.submitEdit(continueWithDeliverables);

      return;
    }

    // Oldest selection first, so the parent highlights the first creator the user picked.
    const selected = [...this.selected()].reverse();
    const addedCampaignCreatorsValue = selected.map((item) => {
      const campaignCreatorForm = this.creatorsAndCtasService.getFilledCampaignCreatorForm(
        undefined,
        item.creator,
        this.campaign() ?? null,
      );

      campaignCreatorForm.controls.cost.setValue(this.hasPayout() ? item.cost.value || null : null);

      return campaignCreatorForm.value;
    });

    this.data.campaignCreatorsValue.push(...addedCampaignCreatorsValue);
    this.creatorsAndCtasService.sortCampaignCreatorsValue(this.data.campaignCreatorsValue);

    this.closeDialog({
      addedCreatorIds: selected.map((item) => item.creator.id),
      continueWithDeliverables,
      saved: true,
    });
  }

  public trackRow(_: number, row: ICreatorPickerRow): string {
    return row.creator.id;
  }

  private initEditMode(): void {
    const campaignCreator = this.data.campaignCreatorsValue.find(
      (value) => value.creatorId === this.data.creatorToEditId,
    );

    if (!campaignCreator?.creator) {
      return;
    }

    this.selected.set([
      { cost: new FormControl<string | null>(campaignCreator.cost ?? null), creator: campaignCreator.creator },
    ]);
  }

  private submitEdit(continueWithDeliverables: boolean): void {
    const [editedCreator] = this.selected();
    const campaignCreator = this.data.campaignCreatorsValue.find(
      (value) => value.creatorId === this.data.creatorToEditId,
    );

    if (campaignCreator && this.hasPayout()) {
      campaignCreator.cost = editedCreator.cost.value || null;
    }

    this.closeDialog({
      addedCreatorIds: [editedCreator.creator.id],
      continueWithDeliverables,
      saved: true,
    });
  }

  private toggleRow(row: ICreatorPickerRow): void {
    if (row.alreadyAdded) {
      return;
    }

    this.setRowsSelected([row], !this.selectedIds().has(row.creator.id));
    this.lastClickedCreatorId = row.creator.id;
  }

  private setRowsSelected(rows: ICreatorPickerRow[], shouldSelect: boolean): void {
    const selectableRows = getSelectableRows(rows);

    if (shouldSelect) {
      this.selectCreators(selectableRows.map((row) => row.creator));

      return;
    }

    const creatorIdsToRemove = new Set(selectableRows.map((row) => row.creator.id));

    this.selected.update((selected) => selected.filter((item) => !creatorIdsToRemove.has(item.creator.id)));
  }

  private selectCreators(creators: ICreator[]): void {
    const selectedIds = this.selectedIds();
    const newItems: ISelectedCreator[] = uniqBy(creators, 'id')
      .filter((creator) => !selectedIds.has(creator.id))
      .map((creator) => ({ cost: new FormControl<string | null>(null), creator }));

    if (newItems.length) {
      this.selected.update((selected) => [...newItems, ...selected]);
    }
  }

  // app-budget only re-renders its input for a new control instance, not for setValue() on the
  // current one - so bulk apply swaps in fresh controls.
  private applyCostToAll(cost: string): void {
    this.selected.update((selected) =>
      selected.map((item) => ({ ...item, cost: new FormControl<string | null>(cost) })),
    );
  }

  private focusRow(index: number): void {
    this.activeRowIndex.set(index);

    const focusRenderedRow = (): boolean => {
      const row = this.elementRef.nativeElement.querySelector<HTMLElement>(`[data-row-index="${index}"]`);

      if (!row) {
        return false;
      }

      row.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
      row.scrollIntoView({ block: 'nearest' });

      return true;
    };

    if (focusRenderedRow()) {
      return;
    }

    this.viewport()?.scrollToIndex(index);
    requestAnimationFrame(() => requestAnimationFrame(() => this.activeRowIndex() === index && focusRenderedRow()));
  }

  private openConfirmDialog(data: IConfirmDialogData, onConfirm: () => void): void {
    this.confirmDialogOpen = true;

    this.dialog
      .open(ConfirmDialogComponent, { data, width: '400px' })
      .afterClosed()
      .subscribe((confirmed: boolean | undefined) => {
        this.confirmDialogOpen = false;

        if (confirmed) {
          onConfirm();
        }
      });
  }

  private closeDialog(result?: ICreatorAndBudgetDialogResult): void {
    if (this.dialogClosing) {
      return;
    }

    this.dialogClosing = true;
    this.dialogRef.close(result);
  }

  private subscribeToEscape(): void {
    this.dialogRef
      .keydownEvents()
      .pipe(
        filter((event) => event.key === 'Escape' && !hasModifierKey(event)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        event.preventDefault();
        this.onCancelClick();
      });
  }

  private createSelectionAnnouncementEffect(): void {
    let previousCount = 0;

    effect(() => {
      const count = this.selected().length;

      if (count === previousCount) {
        return;
      }

      previousCount = count;
      untracked(() =>
        this.liveAnnouncer.announce(
          this.translate.instant(
            this.countKey('pages.campaignForm.sections.creatorsAndCtas.creatorAndBudget.selectedAnnouncement', count),
            {
              count,
            },
          ),
          'polite',
        ),
      );
    });
  }

  private subscribeToSearchChanges(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => this.searchTerm.set(term));
  }

  private countKey(key: string, count: number): string {
    return count === 1 ? `${key}One` : key;
  }

  private createLayoutSync(): void {
    // Scroll events stay outside the zone; fades only trigger change detection when they flip.
    effect((onCleanup) => {
      const viewport = this.viewport();

      if (!viewport) {
        return;
      }

      const subscription = merge(
        viewport.renderedRangeStream.pipe(tap((range) => this.renderedRange.set(range))),
        viewport.elementScrolled().pipe(tap(() => this.updateListFades())),
      ).subscribe();

      onCleanup(() => subscription.unsubscribe());
    });

    afterNextRender(() => {
      const resizeObserver = new ResizeObserver(() => this.measureAvailableHeight());
      const host = this.elementRef.nativeElement;

      resizeObserver.observe(host);

      const root = this.pickerRoot()?.nativeElement;

      if (root) {
        resizeObserver.observe(root);
      }

      this.measureAvailableHeight();

      fromEvent(window, 'resize')
        .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.measureAvailableHeight());

      this.destroyRef.onDestroy(() => resizeObserver.disconnect());
    });

    afterRenderEffect(() => {
      this.layout();
      this.filteredRows();
      this.selected();

      const viewport = this.viewport();

      untracked(() => {
        viewport?.checkViewportSize();
        this.updateListFades();
        this.updateTrayFades();
      });

      // The virtual list applies its new content size a frame later.
      requestAnimationFrame(() => this.updateListFades());
    });
  }

  // Sizes are computed rather than left to flexbox so both lists can be snapped to a half row and the
  // modal itself never scrolls. "Fixed" is everything except the two scroll areas we size ourselves.
  private measureAvailableHeight(): void {
    const host = this.elementRef.nativeElement;
    const root = this.pickerRoot()?.nativeElement;
    const container = host.querySelector<HTMLElement>('.side-dialog-container');
    const content = host.querySelector<HTMLElement>('.dialog-content');

    if (!root || !container || !content) {
      return;
    }

    const chromeHeight = container.offsetHeight - content.offsetHeight;
    const fixedHeight =
      root.offsetHeight -
      (this.listBody()?.nativeElement.offsetHeight ?? 0) -
      (this.trayRows()?.nativeElement.offsetHeight ?? 0);
    const maxDialogHeight = Math.min(window.innerHeight - 96, window.innerHeight * 0.9);

    this.availableHeight.set(Math.max(0, Math.floor(maxDialogHeight - chromeHeight - fixedHeight)));
  }

  private updateListFades(): void {
    const viewport = this.viewport();

    if (!viewport) {
      this.setFades(this.listFades, false, false);

      return;
    }

    this.setFades(this.listFades, viewport.measureScrollOffset('top') > 1, viewport.measureScrollOffset('bottom') > 1);
  }

  private updateTrayFades(): void {
    const trayRows = this.trayRows()?.nativeElement;

    if (!trayRows) {
      this.setFades(this.trayFades, false, false);

      return;
    }

    this.setFades(
      this.trayFades,
      trayRows.scrollTop > 1,
      trayRows.scrollHeight - trayRows.clientHeight - trayRows.scrollTop > 1,
    );
  }

  private setFades(fades: WritableSignal<IScrollFades>, top: boolean, bottom: boolean): void {
    if (fades().top !== top || fades().bottom !== bottom) {
      fades.set({ bottom, top });
    }
  }
}
